import { Gavel } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type {
  OrderApprovalStatus,
  OrderFinancialStatus,
  OrderFulfillmentStatus,
  OrderStatus,
} from '@nucleus-commerce/api-client/admin';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { cn } from '@nucleus-commerce/ui/lib/utils';

const WARNING = 'bg-warning/20 text-warning-foreground border-transparent dark:bg-warning/25';
const SUCCESS = 'bg-success/15 text-success border-transparent dark:bg-success/20';
const NEUTRAL = 'border-border text-muted-foreground';
const INFO = 'bg-primary/10 text-foreground border-transparent';
const DANGER = 'bg-destructive/10 text-destructive border-transparent';

const FINANCIAL: Record<OrderFinancialStatus, string> = {
  unpaid: WARNING,
  cod_pending: WARNING,
  pending: WARNING,
  partially_paid: 'bg-warning/10 text-foreground border-warning/60',
  authorized: INFO,
  paid: SUCCESS,
  partially_refunded: NEUTRAL,
  refunded: NEUTRAL,
  voided: DANGER,
};

const FULFILLMENT: Record<OrderFulfillmentStatus, string> = {
  unfulfilled: NEUTRAL,
  on_hold: WARNING,
  in_production: INFO,
  partially_fulfilled: INFO,
  fulfilled: SUCCESS,
};

const STATUS: Record<OrderStatus, string> = {
  open: INFO,
  completed: SUCCESS,
  cancelled: DANGER,
  archived: NEUTRAL,
};

/** Payment state; unpaid is the loud one because staff act on it. */
export function FinancialStatusBadge({
  status,
  className,
}: {
  status: OrderFinancialStatus;
  className?: string;
}) {
  const { t } = useTranslation('orders');
  return (
    <Badge variant="outline" className={cn(FINANCIAL[status], className)}>
      {t(`financialStatus.${status}`)}
    </Badge>
  );
}

export function FulfillmentStatusBadge({
  status,
  className,
}: {
  status: OrderFulfillmentStatus;
  className?: string;
}) {
  const { t } = useTranslation('orders');
  return (
    <Badge variant="outline" className={cn(FULFILLMENT[status], className)}>
      {t(`fulfillmentStatus.${status}`)}
    </Badge>
  );
}

export function OrderStatusBadge({
  status,
  className,
}: {
  status: OrderStatus;
  className?: string;
}) {
  const { t } = useTranslation('orders');
  return (
    <Badge variant="outline" className={cn(STATUS[status], className)}>
      {t(`status.${status}`)}
    </Badge>
  );
}

/** Only rendered for made-to-order work that needs a design sign-off. */
export function ApprovalStatusBadge({
  status,
  className,
}: {
  status: NonNullable<OrderApprovalStatus>;
  className?: string;
}) {
  const { t } = useTranslation('orders');
  return (
    <Badge variant="outline" className={cn(status === 'approved' ? SUCCESS : WARNING, className)}>
      {t(`approvalStatus.${status}`)}
    </Badge>
  );
}

/** The order was placed for a won auction lot (payment method `auction`). */
export function AuctionOrderBadge({ className }: { className?: string }) {
  const { t } = useTranslation('orders');
  return (
    <Badge variant="outline" className={cn(INFO, 'gap-1', className)}>
      <Gavel aria-hidden className="size-3" />
      {t('auction.badge')}
    </Badge>
  );
}
